/**
 * Mağaza Ekip Yönetimi Servisi
 * Satıcılar mağazalarına çalışan ekleyebilir, roller atayabilir.
 * Roller: owner (tam yetki), manager (ürün/sipariş), staff (sadece görüntüleme)
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { createNotification, NOTIFICATION_TYPES } from './notifications';
import logger from '../utils/logger';

const COLLECTION = 'storeMembers';

export const STORE_ROLES = {
  OWNER: 'owner',       // Tam yetki - mağaza sahibi
  MANAGER: 'manager',   // Ürün ve sipariş yönetimi
  STAFF: 'staff'        // Sadece sipariş görüntüleme
};

export const STORE_ROLE_LABELS = {
  [STORE_ROLES.OWNER]: 'Mağaza Sahibi',
  [STORE_ROLES.MANAGER]: 'Yönetici',
  [STORE_ROLES.STAFF]: 'Çalışan'
};

export const INVITE_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected'
};

/**
 * Üyelik süresinin dolup dolmadığını kontrol et
 */
export const isExpired = (member) => {
  if (!member?.expiresAt) return false;
  const expiresAt = member.expiresAt?.toDate ? member.expiresAt.toDate() : new Date(member.expiresAt);
  return expiresAt < new Date();
};

/**
 * Mağaza sahibini storeMembers'a ekle (satıcı onaylandığında çağrılmalı)
 */
export const initStoreOwner = async (storeId, userId) => {
  try {
    const memberDocId = `${storeId}_${userId}`;
    await setDoc(doc(db, COLLECTION, memberDocId), {
      storeId,
      userId,
      role: STORE_ROLES.OWNER,
      status: INVITE_STATUS.ACCEPTED,
      invitedBy: userId,
      invitedAt: serverTimestamp(),
      acceptedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    logger.error('initStoreOwner error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mağazaya e-posta ile çalışan davet et
 */
export const inviteStoreMember = async (storeId, inviterUserId, inviteeEmail, role = STORE_ROLES.STAFF, expiresAt = null) => {
  try {
    if (!storeId || !inviterUserId || !inviteeEmail) {
      return { success: false, error: 'Eksik bilgi.' };
    }

    if (role === STORE_ROLES.OWNER) {
      return { success: false, error: 'Mağaza sahibi rolü atanamaz.' };
    }

    // Davet eden kişi owner mi kontrol et
    const ownerDocRef = doc(db, COLLECTION, `${storeId}_${inviterUserId}`);
    const inviterDoc = await getDoc(ownerDocRef);
    if (!inviterDoc.exists()) {
      // Owner kaydı yoksa ve storeId == inviterUserId ise otomatik oluştur
      if (storeId === inviterUserId) {
        try {
          await setDoc(ownerDocRef, {
            storeId,
            userId: inviterUserId,
            role: STORE_ROLES.OWNER,
            status: INVITE_STATUS.ACCEPTED,
            invitedBy: inviterUserId,
            invitedAt: serverTimestamp(),
            acceptedAt: serverTimestamp()
          });
          logger.log('Owner kaydı otomatik oluşturuldu:', storeId);
        } catch (ownerErr) {
          logger.error('Owner kaydı oluşturulamadı:', ownerErr);
          return { success: false, error: 'Mağaza sahibi kaydı oluşturulamadı. Lütfen tekrar deneyin.' };
        }
      } else {
        return { success: false, error: 'Sadece mağaza sahibi çalışan davet edebilir.' };
      }
    } else if (inviterDoc.data().role !== STORE_ROLES.OWNER) {
      return { success: false, error: 'Sadece mağaza sahibi çalışan davet edebilir.' };
    }

    const normalizedEmail = inviteeEmail.toLowerCase().trim();

    // E-posta format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      return { success: false, error: 'Geçersiz e-posta adresi.' };
    }

    // Kullanıcı var mı kontrol et
    const userQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail));
    const userSnap = await getDocs(userQuery);
    if (userSnap.empty) {
      return { success: false, error: 'Bu e-posta adresiyle kayıtlı kullanıcı bulunamadı.' };
    }
    const inviteeUserId = userSnap.docs[0].id;

    // Aynı e-posta ile zaten bu mağazada davet var mı?
    const existingQuery = query(
      collection(db, COLLECTION),
      where('storeId', '==', storeId),
      where('inviteeEmail', '==', normalizedEmail)
    );
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) {
      const existing = existingSnap.docs[0].data();
      if (existing.status === INVITE_STATUS.PENDING) {
        return { success: false, error: 'Bu e-posta adresine zaten bekleyen bir davet var.' };
      }
      if (existing.status === INVITE_STATUS.ACCEPTED && !isExpired(existing)) {
        return { success: false, error: 'Bu kişi zaten mağaza ekibinde.' };
      }
    }

    // Tek mağaza kuralı: Bu e-posta başka bir mağazada aktif mi?
    const otherStoreQuery = query(
      collection(db, COLLECTION),
      where('inviteeEmail', '==', normalizedEmail),
      where('status', '==', INVITE_STATUS.ACCEPTED)
    );
    const otherStoreSnap = await getDocs(otherStoreQuery);
    const activeInOtherStore = otherStoreSnap.docs.find(d => {
      const data = d.data();
      return data.storeId !== storeId && !isExpired(data);
    });
    if (activeInOtherStore) {
      return { success: false, error: 'Bu kişi zaten başka bir mağazada çalışıyor.' };
    }

    // expiresAt'i Timestamp'e çevir
    let expiresAtTimestamp = null;
    if (expiresAt) {
      expiresAtTimestamp = Timestamp.fromDate(new Date(expiresAt));
    }

    // Mağaza adını çek (davet dokümanına kaydet — profil sayfasında ayrı sorgu gerekmez)
    let storeName = '';
    try {
      const sellerDoc = await getDoc(doc(db, 'sellerProfiles', storeId));
      if (sellerDoc.exists()) {
        storeName = sellerDoc.data().publicName || '';
      }
      if (!storeName) {
        const userDoc = await getDoc(doc(db, 'users', storeId));
        if (userDoc.exists()) {
          const u = userDoc.data();
          storeName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        }
      }
    } catch (e) { /* ignore */ }

    // Davet oluştur
    const inviteId = `${storeId}_invite_${Date.now()}`;
    await setDoc(doc(db, COLLECTION, inviteId), {
      storeId,
      storeName: storeName || '',
      inviteeEmail: normalizedEmail,
      userId: null,
      role,
      status: INVITE_STATUS.PENDING,
      invitedBy: inviterUserId,
      invitedAt: serverTimestamp(),
      acceptedAt: null,
      expiresAt: expiresAtTimestamp
    });

    // Davet edilen kişiye bildirim gönder
    try {
      await createNotification(
        inviteeUserId,
        NOTIFICATION_TYPES.GENERAL,
        'Mağaza Daveti',
        `${storeName || 'Bir mağaza'} sizi ${STORE_ROLE_LABELS[role]} olarak davet etti.`,
        { link: '/profile' }
      );
    } catch (e) { /* ignore */ }

    return { success: true, inviteId, message: 'Davet gönderildi.' };
  } catch (error) {
    logger.error('inviteStoreMember error:', error);
    return { success: false, error: error.message || 'Davet gönderilemedi.' };
  }
};

/**
 * Kullanıcının bekleyen davetlerini getir (giriş yapan kullanıcının e-postasına göre)
 */
export const getMyInvitations = async (email) => {
  try {
    if (!email) return { success: true, data: [] };
    const q = query(
      collection(db, COLLECTION),
      where('inviteeEmail', '==', email.toLowerCase().trim()),
      where('status', '==', INVITE_STATUS.PENDING)
    );
    const snap = await getDocs(q);
    const invitations = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(i => !isExpired(i));
    return { success: true, data: invitations };
  } catch (error) {
    logger.error('getMyInvitations error:', error);
    return { success: true, data: [] };
  }
};

/**
 * Daveti kabul et
 */
export const acceptInvitation = async (inviteId, userId, userEmail) => {
  try {
    const inviteRef = doc(db, COLLECTION, inviteId);
    const inviteSnap = await getDoc(inviteRef);
    if (!inviteSnap.exists()) {
      return { success: false, error: 'Davet bulunamadı.' };
    }

    const invite = inviteSnap.data();
    if (invite.status !== INVITE_STATUS.PENDING) {
      return { success: false, error: 'Bu davet artık geçerli değil.' };
    }

    // E-posta eşleşme kontrolü — sadece davet sahibi kabul edebilir
    if (userEmail && invite.inviteeEmail && userEmail.toLowerCase().trim() !== invite.inviteeEmail) {
      return { success: false, error: 'Bu daveti kabul etme yetkiniz yok.' };
    }

    // Expire kontrolü
    if (isExpired(invite)) {
      return { success: false, error: 'Bu davetin süresi dolmuş.' };
    }

    // Tek mağaza kuralı: Kullanıcı zaten başka mağazada aktif mi?
    if (userEmail) {
      const otherQuery = query(
        collection(db, COLLECTION),
        where('inviteeEmail', '==', userEmail.toLowerCase().trim()),
        where('status', '==', INVITE_STATUS.ACCEPTED)
      );
      const otherSnap = await getDocs(otherQuery);
      const activeElsewhere = otherSnap.docs.find(d => {
        const data = d.data();
        return data.storeId !== invite.storeId && !isExpired(data);
      });
      if (activeElsewhere) {
        return { success: false, error: 'Zaten başka bir mağazada çalışıyorsunuz. Önce mevcut üyeliğinizin sona ermesi gerekiyor.' };
      }
    }

    await updateDoc(inviteRef, {
      userId,
      status: INVITE_STATUS.ACCEPTED,
      acceptedAt: serverTimestamp()
    });

    // Mağaza sahibine bildirim
    try {
      await createNotification(
        invite.invitedBy,
        NOTIFICATION_TYPES.GENERAL,
        'Ekip Daveti Kabul Edildi',
        `${invite.inviteeEmail} mağazanıza katıldı.`,
        { link: '/seller-dashboard' }
      );
    } catch (e) { /* ignore */ }

    return { success: true, message: 'Daveti kabul ettiniz.' };
  } catch (error) {
    logger.error('acceptInvitation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Daveti reddet
 */
export const rejectInvitation = async (inviteId) => {
  try {
    const inviteRef = doc(db, COLLECTION, inviteId);
    await updateDoc(inviteRef, {
      status: INVITE_STATUS.REJECTED
    });
    return { success: true, message: 'Davet reddedildi.' };
  } catch (error) {
    logger.error('rejectInvitation error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mağazanın tüm üyelerini getir
 */
export const getStoreMembers = async (storeId) => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('storeId', '==', storeId)
    );
    const snap = await getDocs(q);
    const members = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return { success: true, data: members };
  } catch (error) {
    logger.error('getStoreMembers error:', error);
    return { success: true, data: [] };
  }
};

/**
 * Çalışan rolünü güncelle (sadece owner yapabilir)
 */
export const updateMemberRole = async (memberId, newRole, requesterId, storeId) => {
  try {
    if (newRole === STORE_ROLES.OWNER) {
      return { success: false, error: 'Owner rolü bu şekilde atanamaz. Mağaza devri kullanın.' };
    }

    // Requester owner mi kontrol et
    const requesterDoc = await getDoc(doc(db, COLLECTION, `${storeId}_${requesterId}`));
    if (!requesterDoc.exists() || requesterDoc.data().role !== STORE_ROLES.OWNER) {
      return { success: false, error: 'Sadece mağaza sahibi rol değiştirebilir.' };
    }

    const memberRef = doc(db, COLLECTION, memberId);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      return { success: false, error: 'Üye bulunamadı.' };
    }

    if (memberSnap.data().role === STORE_ROLES.OWNER) {
      return { success: false, error: 'Mağaza sahibinin rolü değiştirilemez.' };
    }

    await updateDoc(memberRef, { role: newRole });
    return { success: true, message: 'Rol güncellendi.' };
  } catch (error) {
    logger.error('updateMemberRole error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Çalışanı mağazadan çıkar (sadece owner yapabilir)
 */
export const removeMember = async (memberId, requesterId, storeId) => {
  try {
    const requesterDoc = await getDoc(doc(db, COLLECTION, `${storeId}_${requesterId}`));
    if (!requesterDoc.exists() || requesterDoc.data().role !== STORE_ROLES.OWNER) {
      return { success: false, error: 'Sadece mağaza sahibi üye çıkarabilir.' };
    }

    const memberRef = doc(db, COLLECTION, memberId);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      return { success: false, error: 'Üye bulunamadı.' };
    }

    if (memberSnap.data().role === STORE_ROLES.OWNER) {
      return { success: false, error: 'Mağaza sahibi çıkarılamaz.' };
    }

    await deleteDoc(memberRef);
    return { success: true, message: 'Üye mağazadan çıkarıldı.' };
  } catch (error) {
    logger.error('removeMember error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Üyelik bitiş tarihini güncelle (sadece owner yapabilir)
 */
export const updateMemberExpiry = async (memberId, expiresAt, requesterId, storeId) => {
  try {
    const requesterDoc = await getDoc(doc(db, COLLECTION, `${storeId}_${requesterId}`));
    if (!requesterDoc.exists() || requesterDoc.data().role !== STORE_ROLES.OWNER) {
      return { success: false, error: 'Sadece mağaza sahibi bitiş tarihi değiştirebilir.' };
    }

    const memberRef = doc(db, COLLECTION, memberId);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      return { success: false, error: 'Üye bulunamadı.' };
    }

    if (memberSnap.data().role === STORE_ROLES.OWNER) {
      return { success: false, error: 'Mağaza sahibinin bitiş tarihi ayarlanamaz.' };
    }

    const expiresAtTimestamp = expiresAt ? Timestamp.fromDate(new Date(expiresAt)) : null;
    await updateDoc(memberRef, { expiresAt: expiresAtTimestamp });
    return { success: true, message: 'Bitiş tarihi güncellendi.' };
  } catch (error) {
    logger.error('updateMemberExpiry error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcının aktif mağaza üyeliğini getir (userId ile)
 */
export const getStoreMembershipByUserId = async (userId) => {
  try {
    if (!userId) return { success: true, data: null };
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('status', '==', INVITE_STATUS.ACCEPTED)
    );
    const snap = await getDocs(q);
    const memberships = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(m => m.role !== STORE_ROLES.OWNER && !isExpired(m));

    return { success: true, data: memberships.length > 0 ? memberships[0] : null };
  } catch (error) {
    logger.error('getStoreMembershipByUserId error:', error);
    return { success: true, data: null };
  }
};

/**
 * Kullanıcının belirli bir mağazadaki rolünü getir
 * Owner, Manager veya Staff. Expire kontrolü dahil.
 */
export const getUserStoreRole = async (storeId, userId) => {
  try {
    if (!storeId || !userId) return { success: true, role: null };

    // Önce owner mi kontrol et
    const ownerDocId = `${storeId}_${userId}`;
    const ownerDoc = await getDoc(doc(db, COLLECTION, ownerDocId));
    if (ownerDoc.exists() && ownerDoc.data().role === STORE_ROLES.OWNER) {
      return { success: true, role: STORE_ROLES.OWNER };
    }

    // Değilse invite üzerinden bak
    const q = query(
      collection(db, COLLECTION),
      where('storeId', '==', storeId),
      where('userId', '==', userId),
      where('status', '==', INVITE_STATUS.ACCEPTED)
    );
    const snap = await getDocs(q);
    const membership = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .find(m => !isExpired(m));

    return { success: true, role: membership?.role || null };
  } catch (error) {
    logger.error('getUserStoreRole error:', error);
    return { success: true, role: null };
  }
};

/**
 * Rol bazlı yetki kontrolü
 */
export const ROLE_PERMISSIONS = {
  [STORE_ROLES.OWNER]: ['overview', 'sales', 'orders', 'reports', 'team', 'store', 'profile', 'products.manage'],
  [STORE_ROLES.MANAGER]: ['overview', 'sales', 'orders', 'reports', 'products.manage'],
  [STORE_ROLES.STAFF]: ['overview', 'orders'],
};

export const hasPermission = (role, permission) => {
  if (!role || !ROLE_PERMISSIONS[role]) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
};

/**
 * Mağaza sahipliğini başka bir üyeye devret
 */
export const transferOwnership = async (storeId, currentOwnerId, newOwnerId) => {
  try {
    const currentOwnerDocId = `${storeId}_${currentOwnerId}`;
    const currentOwnerRef = doc(db, COLLECTION, currentOwnerDocId);
    const currentOwnerSnap = await getDoc(currentOwnerRef);

    if (!currentOwnerSnap.exists() || currentOwnerSnap.data().role !== STORE_ROLES.OWNER) {
      return { success: false, error: 'Yetkiniz yok.' };
    }

    // Yeni owner mevcut bir üye mi?
    const newOwnerQuery = query(
      collection(db, COLLECTION),
      where('storeId', '==', storeId),
      where('userId', '==', newOwnerId),
      where('status', '==', INVITE_STATUS.ACCEPTED)
    );
    const newOwnerSnap = await getDocs(newOwnerQuery);
    if (newOwnerSnap.empty) {
      return { success: false, error: 'Bu kişi mağaza ekibinde değil.' };
    }

    // Eski owner'ı manager yap
    await updateDoc(currentOwnerRef, { role: STORE_ROLES.MANAGER });

    // Yeni owner'ı owner yap
    const newOwnerDoc = newOwnerSnap.docs[0];
    await updateDoc(doc(db, COLLECTION, newOwnerDoc.id), { role: STORE_ROLES.OWNER });

    return { success: true, message: 'Mağaza sahipliği devredildi.' };
  } catch (error) {
    logger.error('transferOwnership error:', error);
    return { success: false, error: error.message };
  }
};
